import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag, Folder } from 'lucide-react';
import type { WordPressDraftListItem } from '../types/wordpress';

interface BlogPostCardProps {
  post: WordPressDraftListItem;
}

export default function BlogPostCard({ post }: BlogPostCardProps) {
  const featuredImage =
    post.featured_image?.urls?.large?.url ||
    post.featured_image?.urls?.full?.url ||
    post.featured_image?.urls?.medium?.url ||
    post.featured_image?.urls?.thumbnail?.url ||
    undefined;
  const excerpt = post.excerpt || '';
  const categories = post.categories || [];
  const tags = post.tags || [];
  const authorName = post.author?.name || '';

  if (!featuredImage) {
    console.log('[BlogPostCard] sem imagem destacada', {
      postId: post.id,
      slug: post.slug,
      featuredImage: post.featured_image,
    });
  }
  
  // Formatar data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  return (
    <article className="group bg-[#0a1a2e] rounded-lg overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-xl">
      {/* Imagem destacada */}
      {featuredImage && (
        <Link to={`/blog/${post.slug}`} className="block overflow-hidden">
          <div className="relative h-48 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
            <img
              src={featuredImage}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#081428] via-transparent to-transparent opacity-60" />
          </div>
        </Link>
      )}

      <div className="p-6">
        {(categories.length > 0 || tags.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {categories.slice(0, 2).map((cat, idx) => (
              <span
                key={`cat-${idx}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-500/10 text-blue-200 rounded-full border border-blue-500/30"
              >
                <Folder className="w-3 h-3" />
                {cat.name}
              </span>
            ))}
            {tags.slice(0, 2).map((tag, idx) => (
              <span
                key={`tag-${idx}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-500/10 text-green-200 rounded-full border border-green-500/30"
              >
                <Tag className="w-3 h-3" />
                {tag.name}
              </span>
            ))}
          </div>
        )}
        {/* Título */}
        <Link to={`/blog/${post.slug}`}>
          <h2 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-200 line-clamp-2">
            {post.title}
          </h2>
        </Link>

        {/* Excerpt */}
        <p className="text-gray-400 text-sm mb-4 line-clamp-3">
          {excerpt}
        </p>

        {/* Meta informações */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 mb-4">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatDate(post.modified_gmt || new Date().toISOString())}</span>
          </div>
          {authorName && <span>Por {authorName}</span>}
        </div>

        {/* Link para ler mais */}
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium text-sm group/link transition-colors"
        >
          Ler mais
          <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
        </Link>
      </div>
    </article>
  );
}

